import { useMutation, useQuery } from "convex/react";
import {
	AlertCircle,
	CheckCircle2,
	ChevronRight,
	Globe,
	Loader2,
	Save,
	Search,
} from "lucide-react";
import { useMemo, useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Input } from "../../components/ui/input";
import { MarkdownEditor } from "../../components/ui/markdown-editor";
import { MarkdownView } from "../../components/ui/markdown-view";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Skeleton } from "../../components/ui/skeleton";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../components/ui/tabs";
import { formatRelativeTime } from "../../lib/time";

// My Codex — editable profiles for the current tenant

function ProfileEditor({
	label,
	description,
	loading,
	content,
	onSave,
}: {
	label: string;
	description: string;
	loading: boolean;
	content: string | null;
	onSave: (content: string) => Promise<void>;
}) {
	const [value, setValue] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	const effectiveValue = value ?? content ?? "";
	const isDirty = value !== null && value !== (content ?? "");

	const handleSave = async () => {
		setSaving(true);
		setSaved(false);
		try {
			await onSave(effectiveValue);
			setValue(null);
			setSaved(true);
			setTimeout(() => setSaved(false), 2000);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<div>
					<h4 className="text-sm font-medium">{label}</h4>
					<p className="text-xs text-muted-foreground">{description}</p>
				</div>
				<div className="flex items-center gap-2">
					{saved && (
						<span className="flex items-center gap-1 text-success text-xs">
							<CheckCircle2 className="h-3 w-3" />
							Saved
						</span>
					)}
					<Button
						size="sm"
						variant="outline"
						onClick={handleSave}
						disabled={saving || !isDirty || loading}
					>
						{saving ? (
							<Loader2 className="mr-1 h-3 w-3 animate-spin" />
						) : (
							<Save className="mr-1 h-3 w-3" />
						)}
						Save
					</Button>
				</div>
			</div>
			{loading ? (
				<div className="flex flex-col gap-3 rounded-lg border border-input p-4">
					<Skeleton className="h-5 w-1/4" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-5 w-1/3 mt-2" />
					<Skeleton className="h-4 w-5/6" />
					<Skeleton className="h-4 w-2/3" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-4/5" />
				</div>
			) : (
				<MarkdownEditor
					value={effectiveValue}
					onChange={(md) => setValue(md)}
					placeholder={`No ${label.toLowerCase()} configured yet.`}
				/>
			)}
		</div>
	);
}

type TabKey = "firm" | "person" | "notes";

const ADMIN_TABS: { key: TabKey; label: string }[] = [
	{ key: "firm", label: "Firm Profile" },
	{ key: "person", label: "Person Profile" },
	{ key: "notes", label: "Notes" },
];

const MEMBER_TABS: { key: TabKey; label: string }[] = [
	{ key: "person", label: "Person Profile" },
	{ key: "notes", label: "Notes" },
];

function MyCodexSection() {
	const userRole = useQuery(api.codex.queries.getUserRole);
	const firmProfile = useQuery(api.codex.queries.getFirmProfile);
	const personProfile = useQuery(api.codex.queries.getPersonProfile);

	const saveFirmProfile = useMutation(api.codex.mutations.saveFirmProfile);
	const savePersonProfile = useMutation(api.codex.mutations.savePersonProfile);

	const isAdmin = userRole === "admin";
	const tabs = isAdmin ? ADMIN_TABS : MEMBER_TABS;
	const defaultTab: TabKey = isAdmin ? "firm" : "person";

	return (
		<Card>
			<CardHeader>
				<CardTitle>My Codex</CardTitle>
				<CardDescription>
					These profiles define how Clerical understands your firm and
					communicates on your behalf. Edit them anytime.
				</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-4">
				<Tabs defaultValue={defaultTab} className="flex flex-col gap-4">
					<TabsList className="w-full">
						{tabs.map((tab) => (
							<TabsTrigger key={tab.key} value={tab.key}>
								{tab.label}
							</TabsTrigger>
						))}
					</TabsList>

					{isAdmin && (
						<TabsContent value="firm">
							<ProfileEditor
								label="Firm Profile"
								description="Your firm's name, practice areas, team, and routing rules."
								loading={firmProfile === undefined}
								content={firmProfile?.content ?? null}
								onSave={async (content) => {
									await saveFirmProfile({
										name: firmProfile?.name ?? "Firm Profile",
										content,
									});
								}}
							/>
						</TabsContent>
					)}
					<TabsContent value="person">
						<ProfileEditor
							label="Person Profile"
							description="How you want the agent to draft emails on your behalf — tone, sign-off style, CC rules, and any phrases or topics to avoid."
							loading={personProfile === undefined}
							content={personProfile?.content ?? null}
							onSave={async (content) => {
								await savePersonProfile({
									name: personProfile?.name ?? "Person Profile",
									content,
								});
							}}
						/>
					</TabsContent>
					<TabsContent value="notes">
						<NotesList />
					</TabsContent>
				</Tabs>
			</CardContent>
		</Card>
	);
}

// Notes — read-only view of agent's persistent memory (codexNotes table)

function NotesList() {
	const notes = useQuery(api.codex.notes.list);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

	const availableTags = useMemo(() => {
		if (!notes) return [];
		const all = new Set<string>();
		for (const n of notes) for (const t of n.tags) all.add(t);
		return [...all].sort();
	}, [notes]);

	const filtered = useMemo(() => {
		if (!notes) return [];
		const needle = searchTerm.trim().toLowerCase();
		return notes.filter((n) => {
			if (selectedTags.size > 0) {
				for (const t of selectedTags) if (!n.tags.includes(t)) return false;
			}
			if (!needle) return true;
			const hay =
				`${n.description} ${n.slug} ${n.tags.join(" ")}`.toLowerCase();
			return hay.includes(needle);
		});
	}, [notes, searchTerm, selectedTags]);

	const toggleTag = (tag: string) => {
		setSelectedTags((prev) => {
			const next = new Set(prev);
			if (next.has(tag)) next.delete(tag);
			else next.add(tag);
			return next;
		});
	};

	if (notes === undefined) {
		return (
			<div className="flex flex-col gap-2">
				<Skeleton className="h-9 w-full" />
				<Skeleton className="h-12 w-full" />
				<Skeleton className="h-12 w-full" />
				<Skeleton className="h-12 w-full" />
			</div>
		);
	}

	if (notes.length === 0) {
		return (
			<div className="flex flex-col gap-2">
				<h4 className="text-sm font-medium">Notes</h4>
				<p className="text-xs text-muted-foreground">
					Clerical hasn't written any notes for your firm yet. As it learns
					filing systems, naming conventions, and SOP details, they'll appear
					here.
				</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			<div>
				<h4 className="text-sm font-medium">Notes</h4>
				<p className="text-xs text-muted-foreground">
					Clerical's persistent memory for your firm — filing systems, naming
					conventions, and SOP details it has learned. Read-only; the agent
					updates these as it works.
				</p>
			</div>

			<div className="relative">
				<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
				<Input
					placeholder="Search by description, slug, or tag…"
					value={searchTerm}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setSearchTerm(e.target.value)
					}
					className="pl-8"
				/>
			</div>

			{availableTags.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{availableTags.map((tag) => {
						const active = selectedTags.has(tag);
						return (
							<button
								type="button"
								key={tag}
								onClick={() => toggleTag(tag)}
								className="cursor-pointer"
							>
								<Badge variant={active ? "default" : "outline"}>{tag}</Badge>
							</button>
						);
					})}
				</div>
			)}

			{filtered.length === 0 ? (
				<p className="py-6 text-center text-xs text-muted-foreground">
					No notes match the current filter.
				</p>
			) : (
				<div className="flex flex-col gap-2">
					{filtered.map((note) => (
						<NoteRow key={note.slug} note={note} />
					))}
				</div>
			)}
		</div>
	);
}

function NoteRow({
	note,
}: {
	note: {
		slug: string;
		description: string;
		tags: string[];
		updatedAt: number;
	};
}) {
	const [expanded, setExpanded] = useState(false);
	const detail = useQuery(
		api.codex.notes.getBySlug,
		expanded ? { slug: note.slug } : "skip",
	);

	return (
		<Collapsible open={expanded} onOpenChange={setExpanded}>
			<div className="rounded-lg border">
				<CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left hover:bg-muted/50">
					<ChevronRight
						className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`}
					/>
					<div className="flex min-w-0 flex-1 flex-col gap-0.5">
						<div className="flex items-center gap-2">
							<span className="truncate font-mono text-sm font-medium">
								{note.slug}
							</span>
						</div>
						<p className="truncate text-xs text-muted-foreground">
							{note.description}
						</p>
						{note.tags.length > 0 && (
							<div className="flex flex-wrap gap-1">
								{note.tags.map((tag) => (
									<Badge key={tag} variant="secondary">
										{tag}
									</Badge>
								))}
							</div>
						)}
					</div>
					<span className="shrink-0 text-xs text-muted-foreground">
						{formatRelativeTime(note.updatedAt)}
					</span>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<div className="border-t px-3 py-3">
						{detail === undefined ? (
							<div className="flex flex-col gap-2">
								<Skeleton className="h-4 w-3/4" />
								<Skeleton className="h-4 w-full" />
								<Skeleton className="h-4 w-2/3" />
							</div>
						) : detail === null ? (
							<p className="text-xs text-muted-foreground">
								This note was deleted.
							</p>
						) : (
							<MarkdownView value={detail.body} />
						)}
					</div>
				</CollapsibleContent>
			</div>
		</Collapsible>
	);
}

// Admin: tenant discovery trigger, super-admin only

function TenantRow({ tenantId }: { tenantId: Id<"tenants"> }) {
	const discovery = useQuery(api.codex.admin.getDiscoveryForTenant, {
		targetTenantId: tenantId,
	});

	if (!discovery) return null;

	const statusColor = {
		pending: "text-warning",
		crawling: "text-primary",
		extracting: "text-primary",
		complete: "text-success",
		failed: "text-destructive",
	}[discovery.status];

	return (
		<div className="mt-2 rounded border bg-muted/50 p-3 text-sm">
			<div className="flex items-center justify-between">
				<span className="text-muted-foreground">{discovery.websiteUrl}</span>
				<span className={`font-medium ${statusColor}`}>
					{discovery.status === "crawling" ||
					discovery.status === "extracting" ? (
						<span className="flex items-center gap-1">
							<Loader2 className="h-3 w-3 animate-spin" />
							{discovery.status}
						</span>
					) : discovery.status === "complete" ? (
						<span className="flex items-center gap-1">
							<CheckCircle2 className="h-3 w-3" />
							complete
						</span>
					) : discovery.status === "failed" ? (
						<span className="flex items-center gap-1">
							<AlertCircle className="h-3 w-3" />
							failed
						</span>
					) : (
						discovery.status
					)}
				</span>
			</div>
			{discovery.error && (
				<p className="mt-1 text-destructive text-xs">{discovery.error}</p>
			)}
			{discovery.extractedFirm && (
				<Collapsible className="mt-2">
					<CollapsibleTrigger className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
						View extracted firm data
					</CollapsibleTrigger>
					<CollapsibleContent>
						<ScrollArea
							orientation="both"
							className="mt-1 max-h-48 rounded bg-background"
						>
							<pre className="p-2 text-xs">
								{(() => {
									try {
										return JSON.stringify(
											JSON.parse(discovery.extractedFirm),
											null,
											2,
										);
									} catch {
										return discovery.extractedFirm;
									}
								})()}
							</pre>
						</ScrollArea>
					</CollapsibleContent>
				</Collapsible>
			)}
			{discovery.extractedTeam && (
				<Collapsible className="mt-2">
					<CollapsibleTrigger className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
						View extracted team data
					</CollapsibleTrigger>
					<CollapsibleContent>
						<ScrollArea
							orientation="both"
							className="mt-1 max-h-48 rounded bg-background"
						>
							<pre className="p-2 text-xs">
								{(() => {
									try {
										return JSON.stringify(
											JSON.parse(discovery.extractedTeam),
											null,
											2,
										);
									} catch {
										return discovery.extractedTeam;
									}
								})()}
							</pre>
						</ScrollArea>
					</CollapsibleContent>
				</Collapsible>
			)}
		</div>
	);
}

function TenantCard({
	tenant,
}: {
	tenant: {
		_id: Id<"tenants">;
		name: string;
		slug: string;
		codexStatus: string | null;
	};
}) {
	const triggerDiscovery = useMutation(api.codex.admin.triggerDiscovery);
	const [websiteUrl, setWebsiteUrl] = useState("");
	const [isTriggering, setIsTriggering] = useState(false);
	const [showDiscovery, setShowDiscovery] = useState(false);

	const handleTrigger = async () => {
		if (!websiteUrl) return;
		setIsTriggering(true);
		try {
			const url = websiteUrl.startsWith("http")
				? websiteUrl
				: `https://${websiteUrl}`;
			await triggerDiscovery({
				targetTenantId: tenant._id,
				websiteUrl: url,
			});
			setShowDiscovery(true);
		} finally {
			setIsTriggering(false);
		}
	};

	const statusBadge = {
		complete: (
			<span className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
				Complete
			</span>
		),
		in_progress: (
			<span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
				In Progress
			</span>
		),
		pending: (
			<span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning">
				Pending
			</span>
		),
	}[tenant.codexStatus ?? ""] ?? (
		<span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
			Not Started
		</span>
	);

	return (
		<div className="rounded-lg border p-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
						<Globe className="h-4 w-4 text-primary" />
					</div>
					<div>
						<p className="font-medium">{tenant.name}</p>
						<p className="text-xs text-muted-foreground">{tenant.slug}</p>
					</div>
				</div>
				{statusBadge}
			</div>

			<div className="mt-3 flex gap-2">
				<Input
					placeholder="https://firmwebsite.com"
					value={websiteUrl}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setWebsiteUrl(e.target.value)
					}
					className="flex-1"
				/>
				<Button
					size="sm"
					onClick={handleTrigger}
					disabled={!websiteUrl || isTriggering}
				>
					{isTriggering ? (
						<Loader2 className="mr-1 h-3 w-3 animate-spin" />
					) : (
						<Search className="mr-1 h-3 w-3" />
					)}
					Discover
				</Button>
			</div>

			{(showDiscovery || tenant.codexStatus === "in_progress") && (
				<TenantRow tenantId={tenant._id} />
			)}
		</div>
	);
}

function AdminSection() {
	const tenants = useQuery(api.codex.admin.listManagedTenants);

	return (
		<Card>
			<CardHeader>
				<CardTitle>Manage Tenants</CardTitle>
				<CardDescription>
					Trigger website discovery crawls to pre-fill Codex profiles for
					organizations.
				</CardDescription>
			</CardHeader>
			<CardContent>
				{tenants === undefined ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					</div>
				) : tenants.length === 0 ? (
					<p className="py-8 text-center text-muted-foreground">
						No tenants found.
					</p>
				) : (
					<div className="flex flex-col gap-4">
						{tenants.map((tenant) => (
							<TenantCard key={tenant._id} tenant={tenant} />
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

export default function CodexPage() {
	const isSuperAdmin = useQuery(api.codex.admin.isSuperAdminTenant);

	return (
		<ScrollArea className="h-full">
			<div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
				<MyCodexSection />
				{isSuperAdmin && <AdminSection />}
			</div>
		</ScrollArea>
	);
}
